import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  ScatterController,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Bar, Line, Scatter } from 'react-chartjs-2';
import {
  ChartSpec,
  PointsSpec,
  SeriesSpec,
  StatCardsSpec,
  TableSpec,
  TimelineSpec,
  parseChartBlock,
} from './parseChartBlock';
import { printMessage } from './printExport';

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  ScatterController,
  ChartTooltip
);

const SERIES_COLORS = ['#0088CF', '#00B7B2', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed'];

const AiDataTable = ({ spec, expanded }: { spec: TableSpec; expanded?: boolean }) => {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const { normalRows, pinnedRows } = useMemo(() => {
    const normal = spec.rows.filter((r) => !r._pinned);
    const pinned = spec.rows.filter((r) => r._pinned);
    if (sortKey) {
      normal.sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
        return sortAsc
          ? String(av ?? '').localeCompare(String(bv ?? ''))
          : String(bv ?? '').localeCompare(String(av ?? ''));
      });
    }
    return { normalRows: normal, pinnedRows: pinned };
  }, [spec.rows, sortKey, sortAsc]);

  const onSort = (key: string) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  return (
    <TableContainer
      sx={{
        maxHeight: expanded ? '70vh' : 420,
        border: '1px solid #e4e7eb',
        borderRadius: 1,
        my: 1,
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {spec.columns.map((column) => (
              <TableCell key={column.key} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortKey === column.key}
                  direction={sortKey === column.key && !sortAsc ? 'desc' : 'asc'}
                  onClick={() => onSort(column.key)}
                >
                  {column.label ?? column.key}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...normalRows, ...pinnedRows].map((row, index) => (
            <TableRow
              key={index}
              sx={row._pinned ? { '& td': { fontWeight: 700, borderTop: '2px solid #cbd2d9' } } : undefined}
            >
              {spec.columns.map((column) => (
                <TableCell key={column.key}>
                  {row[column.key] === undefined || row[column.key] === null
                    ? ''
                    : String(row[column.key])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AiSeriesChart = ({ spec, expanded }: { spec: SeriesSpec; expanded?: boolean }) => {
  const stacked = spec.type === 'stacked_bar_chart';
  const histogram = spec.type === 'histogram';
  const data = {
    labels: spec.data.map((row) => String(row[spec.x_key] ?? '')),
    datasets: spec.series.map((series, index) => ({
      label: series.label ?? series.key,
      data: spec.data.map((row) => Number(row[series.key] ?? 0)),
      backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
      borderColor: SERIES_COLORS[index % SERIES_COLORS.length],
      // Histograms render contiguous bars (pre-binned data, no gaps).
      ...(histogram ? { barPercentage: 1.0, categoryPercentage: 1.0 } : {}),
    })),
  };
  const options = {
    responsive: true,
    plugins: { legend: { display: spec.series.length > 1 } },
    scales: { x: { stacked }, y: { stacked, beginAtZero: true } },
  };
  const ChartComponent = spec.type === 'line_chart' ? Line : Bar;
  return (
    <Box sx={{ maxWidth: expanded ? 'none' : 640, my: 1 }}>
      <ChartComponent data={data} options={options} />
    </Box>
  );
};

// scatter_plot: numeric x/y. dot_chart: numeric x against category y.
const AiPointsChart = ({ spec, expanded }: { spec: PointsSpec; expanded?: boolean }) => {
  const isDot = spec.type === 'dot_chart';
  const yCategories = isDot
    ? [...new Set(spec.data.map((row) => String(row[spec.y_key] ?? '')))]
    : undefined;
  const data = {
    datasets: [
      {
        label: spec.title ?? spec.y_key,
        data: spec.data.map((row) => ({
          x: Number(row[spec.x_key] ?? 0),
          y: isDot ? String(row[spec.y_key] ?? '') : Number(row[spec.y_key] ?? 0),
        })),
        backgroundColor: SERIES_COLORS[0],
        pointRadius: 5,
      },
    ],
  };
  const options: any = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, title: { display: true, text: spec.x_key } },
      y: isDot ? { type: 'category', labels: yCategories } : { beginAtZero: true },
    },
  };
  return (
    <Box sx={{ maxWidth: expanded ? 'none' : 640, my: 1 }}>
      <Scatter data={data} options={options} />
    </Box>
  );
};

const AiTimeline = ({ spec }: { spec: TimelineSpec }) => (
  <Box sx={{ my: 1, pl: 1 }} data-testid="ai-timeline">
    {spec.events.map((event, index) => (
      <Box
        key={index}
        sx={{
          borderLeft: '2px solid #0088CF',
          pl: 2,
          pb: index === spec.events.length - 1 ? 0 : 1.5,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -5,
            top: 6,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#0088CF',
          },
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {event.date}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {event.label}
        </Typography>
        {event.description ? (
          <Typography variant="body2" color="text.secondary">
            {event.description}
          </Typography>
        ) : null}
      </Box>
    ))}
  </Box>
);

const AiStatCards = ({ spec }: { spec: StatCardsSpec }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1 }}>
    {spec.cards.map((card, index) => (
      <Card key={index} variant="outlined" sx={{ minWidth: 140 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            {card.label}
          </Typography>
          <Typography variant="h6">
            {card.value}
            {card.unit ? (
              <Typography component="span" variant="body2" color="text.secondary" ml={0.5}>
                {card.unit}
              </Typography>
            ) : null}
          </Typography>
        </CardContent>
      </Card>
    ))}
  </Box>
);

const SpecRenderer = ({ spec, expanded }: { spec: ChartSpec; expanded?: boolean }) => (
  <>
    {spec.type === 'table' ? <AiDataTable spec={spec} expanded={expanded} /> : null}
    {spec.type === 'bar_chart' ||
    spec.type === 'stacked_bar_chart' ||
    spec.type === 'grouped_bar_chart' ||
    spec.type === 'line_chart' ||
    spec.type === 'histogram' ? (
      <AiSeriesChart spec={spec} expanded={expanded} />
    ) : null}
    {spec.type === 'scatter_plot' || spec.type === 'dot_chart' ? (
      <AiPointsChart spec={spec} expanded={expanded} />
    ) : null}
    {spec.type === 'timeline' ? <AiTimeline spec={spec} /> : null}
    {spec.type === 'stat_cards' ? <AiStatCards spec={spec} /> : null}
  </>
);

// Types that benefit from the full-screen expand dialog (HemIQ pattern —
// tables and axis charts; stat cards and timelines read fine inline).
const EXPANDABLE_TYPES = new Set([
  'table',
  'bar_chart',
  'stacked_bar_chart',
  'grouped_bar_chart',
  'line_chart',
  'histogram',
  'scatter_plot',
  'dot_chart',
]);

// Renders the JSON body of a ```stackiq-chart fence with per-visualization
// expand + print actions. Invalid or unknown specs get a graceful fallback —
// never raw JSON.
const AiVisualization = ({ raw }: { raw: string }) => {
  const spec: ChartSpec | null = useMemo(() => parseChartBlock(raw), [raw]);
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const dialogBodyRef = useRef<HTMLDivElement>(null);

  if (!spec) {
    return (
      <Box
        data-testid="ai-visualization-fallback"
        sx={{ border: '1px dashed #cbd2d9', borderRadius: 1, p: 1.5, my: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          This visualization could not be displayed.
        </Typography>
      </Box>
    );
  }

  const canExpand = EXPANDABLE_TYPES.has(spec.type);
  const printInline = () => cardRef.current && printMessage(cardRef.current, spec.title ?? 'visualization');
  const printExpanded = () =>
    dialogBodyRef.current && printMessage(dialogBodyRef.current, spec.title ?? 'visualization');

  return (
    <Box ref={cardRef}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} minHeight={26}>
        <Typography variant="subtitle2" sx={{ minWidth: 0 }} noWrap>
          {spec.title ?? ''}
        </Typography>
        <Box className="stackiq-chart-actions" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
          {canExpand ? (
            <Tooltip title="Expand">
              <IconButton size="small" onClick={() => setExpanded(true)} aria-label="Expand visualization">
                <OpenInFullIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Print / save as PDF">
            <IconButton size="small" onClick={printInline} aria-label="Print visualization">
              <PrintIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <SpecRenderer spec={spec} />

      <Dialog open={expanded} onClose={() => setExpanded(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
          <Typography component="span" variant="h6" noWrap>
            {spec.title ?? 'Visualization'}
          </Typography>
          <Box className="stackiq-chart-actions" sx={{ whiteSpace: 'nowrap' }}>
            <Tooltip title="Print / save as PDF">
              <IconButton onClick={printExpanded} aria-label="Print expanded visualization">
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton onClick={() => setExpanded(false)} aria-label="Close expanded visualization">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box ref={dialogBodyRef}>
            <SpecRenderer spec={spec} expanded />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AiVisualization;
